/**
 * src/lib/agent/scopeContract.js — Chat scope contract (pure, no React).
 *
 * The chat scope is the set of grounding entities the CEO has pinned to a
 * conversation. It drives the scope chip row in ChatHeader, the empty-state
 * suggestion card selection, and the context packet sent to /api/ai.
 *
 * Canonical shape (ScopeEntry):
 *   { type: 'project' | 'deal' | 'task' | 'person', id: string, name: string, ... }
 *
 * Port is NOT a scope type. Marinas / ports are inferred from the project's
 * location field at prompt-assembly time — adding port as a first-class chip
 * would duplicate state that lives on the project row.
 */

export const SCOPE_TYPES = Object.freeze(['project', 'deal', 'task', 'person']);

/**
 * Convert a raw Supabase entity row into a ScopeEntry.
 * Consumes snake_case columns directly (Supabase-native rule).
 * Throws on unknown types — silent coercion hides bugs.
 *
 * @param {object} entity - Supabase row
 * @param {'project'|'deal'|'task'|'person'} type
 * @returns {{type:string, id:string, name:string, [k:string]:any}}
 */
export function toScopeEntry(entity, type) {
  if (!entity || typeof entity !== 'object') {
    throw new Error(`scopeContract.toScopeEntry: entity required, got ${entity}`);
  }
  if (!SCOPE_TYPES.includes(type)) {
    throw new Error(`scopeContract.toScopeEntry: unknown scope type '${type}'`);
  }
  const id = entity.id;
  if (!id) {
    throw new Error(`scopeContract.toScopeEntry: entity.id required for type '${type}'`);
  }

  switch (type) {
    case 'project':
      return {
        type: 'project',
        id,
        name: entity.project_name || entity.name || '(untitled project)',
      };
    case 'deal':
      return {
        type: 'deal',
        id,
        // crm_deals uses 'opportunity' as the display name column
        name: entity.deal_name || entity.opportunity || entity.name || '(untitled deal)',
        project_id: entity.project_id ?? null,
        amount: entity.deal_value ?? entity.amount ?? null,
      };
    case 'task':
      return {
        type: 'task',
        id,
        name: entity.task_name || entity.name || '(untitled task)',
        project_id: entity.project_id ?? null,
      };
    case 'person':
      return {
        type: 'person',
        id,
        name: entity.display_name || entity.full_name || entity.email || '(unknown person)',
      };
    default:
      throw new Error(`scopeContract.toScopeEntry: unreachable default for '${type}'`);
  }
}

/**
 * Rebuild a scope array from a chat_conversations row.
 * FK columns (project_id, deal_id, task_id, assigned_person_id) take
 * precedence over `scope` jsonb entries — the DB is the source of truth.
 * Freeform `scope` jsonb entries are merged in for any types not covered by
 * FK columns (future-proof for types we may add without new columns).
 * De-duplicated by `${type}:${id}`.
 *
 * The caller is responsible for hydrating names via their domain hooks —
 * this function does not do DB lookups. FK-only entries come back with
 * name: '' and get populated by the component that has the project/deal/
 * task/person rows in memory.
 *
 * @param {object} row - chat_conversations row
 * @returns {Array<ScopeEntry>}
 */
export function fromConversationRow(row) {
  if (!row || typeof row !== 'object') return [];

  const seen = new Set();
  const out = [];

  const pushIfNew = entry => {
    if (!entry || !entry.id || !entry.type) return;
    const key = `${entry.type}:${entry.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  };

  // FK columns first — authoritative.
  if (row.project_id) {
    pushIfNew({ type: 'project', id: row.project_id, name: '' });
  }
  if (row.deal_id) {
    pushIfNew({ type: 'deal', id: row.deal_id, name: '' });
  }
  if (row.task_id) {
    pushIfNew({ type: 'task', id: row.task_id, name: '' });
  }
  if (row.assigned_person_id) {
    pushIfNew({ type: 'person', id: row.assigned_person_id, name: '' });
  }

  // Freeform scope jsonb — merge remaining entries that are valid and not
  // already represented by FK columns.
  const freeform = Array.isArray(row.scope) ? row.scope : [];
  for (const raw of freeform) {
    if (!raw || typeof raw !== 'object') continue;
    if (!SCOPE_TYPES.includes(raw.type)) continue;
    if (!raw.id) continue;
    pushIfNew({ ...raw });
  }

  return out;
}

/**
 * Convert a scope array back into the DB patch shape — splits into FK
 * columns (first entry per type) + leftover freeform `scope` jsonb for
 * types not represented by FK columns (today: none — all four types have
 * FK columns).
 *
 * @param {Array<ScopeEntry>} scope
 * @returns {{project_id:string|null, deal_id:string|null, task_id:string|null, assigned_person_id:string|null, scope:Array}}
 */
export function toConversationPatch(scope) {
  const patch = {
    project_id: null,
    deal_id: null,
    task_id: null,
    assigned_person_id: null,
    scope: [],
  };

  if (!Array.isArray(scope)) return patch;

  const seen = new Set();
  const overflow = [];

  for (const raw of scope) {
    if (!raw || !raw.type || !raw.id) continue;
    if (!SCOPE_TYPES.includes(raw.type)) {
      overflow.push({ ...raw });
      continue;
    }
    const key = `${raw.type}:${raw.id}`;
    if (seen.has(key)) continue;
    seen.add(key);

    switch (raw.type) {
      case 'project':
        if (!patch.project_id) patch.project_id = raw.id;
        else overflow.push({ ...raw });
        break;
      case 'deal':
        if (!patch.deal_id) patch.deal_id = raw.id;
        else overflow.push({ ...raw });
        break;
      case 'task':
        if (!patch.task_id) patch.task_id = raw.id;
        else overflow.push({ ...raw });
        break;
      case 'person':
        if (!patch.assigned_person_id) patch.assigned_person_id = raw.id;
        else overflow.push({ ...raw });
        break;
      default:
        overflow.push({ ...raw });
    }
  }

  patch.scope = overflow;
  return patch;
}

/**
 * Derive the most-specific ctx from a scope array.
 * Priority: task > deal > project > person > global.
 * This matches the mockup's CTX key: the most specific entity is the "anchor"
 * of the conversation for suggestion cards + prompt framing.
 *
 * @param {Array<ScopeEntry>} scope
 * @returns {'task'|'deal'|'project'|'person'|'global'}
 */
export function deriveCtx(scope) {
  if (!Array.isArray(scope) || scope.length === 0) return 'global';
  const types = new Set(scope.map(e => e?.type).filter(Boolean));
  if (types.has('task')) return 'task';
  if (types.has('deal')) return 'deal';
  if (types.has('project')) return 'project';
  if (types.has('person')) return 'person';
  return 'global';
}

/**
 * De-duplicate a scope array by `${type}:${id}`, preserving order.
 * @param {Array<ScopeEntry>} scope
 * @returns {Array<ScopeEntry>}
 */
export function dedupeScope(scope) {
  if (!Array.isArray(scope)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of scope) {
    if (!entry || !entry.type || !entry.id) continue;
    const key = `${entry.type}:${entry.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}
